import {
  getConfigTenant,
  isCorpTenant,
  travelAppInterface,
  TravelAppInterfaceEventName,
  useIsTravelApp,
  useQueryParams,
} from "@capone/common";
import {
  SpendAndGetOfferWalletSection,
  useSetActiveBusinessAccount,
} from "@capone-corporate/portal";
import { Box } from "@material-ui/core";
import clsx from "clsx";
import {
  B2BSpinner,
  CurrencyFormatters,
  LoadingPopup,
  PageTabNavigations,
  SecondaryHeader,
  TravelSalesEventBanner,
} from "halifax";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useInView } from "react-intersection-observer";
import { RouteComponentProps } from "react-router-dom";
import { CallState } from "redmond";
import {
  ANNUAL_TRAVEL_CREDITS,
  AVAILABLE,
  CONTROL,
  CORP_SPEND_AND_GET_OFFER,
  CREDIT_OFFER_STACKING_V1,
  getExperimentVariant,
  getExperimentVariantCustomVariants,
  TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  TRAVEL_SALE,
  TRAVEL_SALE_ACTIVE,
  TRAVEL_SALE_LEAD_UP,
  TRAVEL_SALE_VARIANTS,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../context/experiments";
import { useDeviceTypes } from "../../hooks/useDeviceTypes";
import { BASE_PATH_HOME, PATH_TRAVEL_SALE } from "../../utils/paths";
import { getStickyTopOffset } from "../../utils/getStickyTopOffset";
import { Credits } from "./components/Credits";
import { FAQ } from "./components/FAQ";
import { OffersList } from "./components/OffersList";
import { Support } from "./components/Support";
import { TravelWalletLandingConnectorProps } from "./container";
import "./styles.scss";
import * as textConstants from "./textConstants";

export interface ITravelWalletLandingProps
  extends TravelWalletLandingConnectorProps,
    RouteComponentProps {
  isMobile?: boolean;
}

export const TravelWalletLanding = ({
  fetchTravelWalletDetails,
  allOffers,
  activeOffers,
  credit,
  areOfferDetailsLoading,
  history,
  fetchTravelWalletCreditHistory,
  fetchRewardsAccounts,
  rewardsAccountsCallState,
  walletDetailsCallState,
}: ITravelWalletLandingProps) => {
  const expState = useExperiments();
  if (isCorpTenant(getConfigTenant())) {
    useSetActiveBusinessAccount();
  }

  const travelCreditHistoryExperiment = getExperimentVariant(
    expState.experiments,
    TRAVEL_CREDIT_HISTORY_EXPERIMENT,
  );
  const isTravelCreditHistoryExperiment = React.useMemo(
    () => travelCreditHistoryExperiment === AVAILABLE,
    [travelCreditHistoryExperiment],
  );

  const creditAndOfferStackingExperimentV1 = getExperimentVariant(
    expState.experiments,
    CREDIT_OFFER_STACKING_V1,
  );
  const isCreditAndOfferStackingExperimentV1 = React.useMemo(
    () => creditAndOfferStackingExperimentV1 === AVAILABLE,
    [creditAndOfferStackingExperimentV1],
  );

  const isAnnualTravelCreditsExperiment =
    getExperimentVariant(expState.experiments, ANNUAL_TRAVEL_CREDITS) ===
    AVAILABLE;

  const travelSaleVariant = getExperimentVariantCustomVariants(
    expState.experiments,
    TRAVEL_SALE,
    TRAVEL_SALE_VARIANTS,
  );

  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  const isSpendAndGetOfferEnabled =
    getExperimentVariant(expState.experiments, CORP_SPEND_AND_GET_OFFER) ===
    AVAILABLE;

  const offers =
    travelSaleVariant === TRAVEL_SALE_LEAD_UP ? activeOffers : allOffers;

  const [clickedSectionId, setClickedSectionId] = useState<string>();

  const [creditsRef, creditsInView] = useInView({ threshold: 0.4 });
  const [offersRef, offersInView] = useInView();
  const [faqRef, faqInView] = useInView();
  const [mobileSectionToDisplay, setMobileSectionToDisplay] = useState<
    "credits" | "offers"
  >("credits");

  const params = useQueryParams();
  const { matchesMobile } = useDeviceTypes();

  const scrollToSection = useCallback(
    (sectionId: string) => {
      if (!matchesMobile) {
        const element = document.getElementById(sectionId);

        const BANNER_HEIGHT =
          document
            .getElementById("b2bportal-banner-container")
            ?.getBoundingClientRect().height ?? 0;
        const HEADER_HEIGHT = getStickyTopOffset();
        const NAV_HEIGHT =
          document.querySelector(".page-nav")?.getBoundingClientRect().height ??
          0;
        const MARGIN = -30;

        const yOffset =
          window.pageYOffset -
          (BANNER_HEIGHT + HEADER_HEIGHT + NAV_HEIGHT + MARGIN);

        const y = (element?.getBoundingClientRect().top ?? 0) + yOffset;

        setClickedSectionId(sectionId);
        setTimeout(() => {
          setClickedSectionId(undefined);
        }, 5000);
        window.scrollTo({ top: y, behavior: "smooth" });
      }
    },
    [matchesMobile],
  );

  const selectedTab = React.useMemo(() => {
    if (
      creditsInView &&
      (clickedSectionId ? clickedSectionId === "credits" : true)
    ) {
      return 0;
    }
    if (faqInView && (clickedSectionId ? clickedSectionId === "faq" : true)) {
      if (matchesMobile) {
        return 0;
      }
      return 2;
    }
    if (
      offersInView &&
      (clickedSectionId ? clickedSectionId === "offers" : true)
    ) {
      return 1;
    }

    return undefined;
  }, [creditsInView, offersInView, faqInView]);

  useEffect(() => {
    fetchTravelWalletDetails();
    fetchRewardsAccounts();
  }, []);

  useEffect(() => {
    if (
      [CallState.Success, CallState.Failed].includes(
        rewardsAccountsCallState,
      ) &&
      [CallState.Success, CallState.Failed].includes(walletDetailsCallState)
    )
      travelAppInterface.sendEvent({
        name: TravelAppInterfaceEventName.PAGE_LOADED,
      });
  }, [rewardsAccountsCallState, walletDetailsCallState]);

  useEffect(() => {
    isTravelCreditHistoryExperiment && fetchTravelWalletCreditHistory();
  }, [isTravelCreditHistoryExperiment]);

  useEffect(() => {
    if (
      (history.location.hash.startsWith("#faq") || params.section === "faq") &&
      !areOfferDetailsLoading
    ) {
      scrollToSection("faq");
      if (matchesMobile) {
        const faqElement = document.getElementById("faq");

        faqElement?.scrollIntoView();
      }
    }
    if (
      (history.location.hash === "#offers" || params.section === "offers") &&
      !areOfferDetailsLoading
    ) {
      scrollToSection("offers");
      if (matchesMobile) {
        window.scrollTo({ top: 0 });
      }
    }
    if (
      (history.location.hash === "#credits" || params.section === "credits") &&
      !areOfferDetailsLoading
    ) {
      scrollToSection("credits");
      if (matchesMobile) {
        window.scrollTo({ top: 0 });
      }
    }
  }, [history, areOfferDetailsLoading, params.section, matchesMobile]);

  const headerBreadCrumbLinks = [
    {
      onClick: () => {
        history.push(BASE_PATH_HOME);
      },
      label: "Search",
    },
    {
      label: textConstants.REDESIGN_HEADER_TITLE_WALLET,
    },
  ];

  const rightContent = useMemo(() => {
    if (travelSaleVariant !== CONTROL) {
      return (
        <TravelSalesEventBanner
          variant={
            travelSaleVariant === TRAVEL_SALE_ACTIVE ? "default" : "with-timer"
          }
          onClick={() => {
            const path = `${PATH_TRAVEL_SALE}?entryType=my_travel_credits_offers`;
            if (matchesMobile) {
              history.push(path);
            } else {
              window.open(path, "_blank");
            }
          }}
          subtitle={
            travelSaleVariant === TRAVEL_SALE_ACTIVE
              ? textConstants.TRAVEL_SALES_EVENT_ACTIVE_SUBTITLE
              : textConstants.TRAVEL_SALES_EVENT_LEADUP_SUBTITLE
          }
          buttonText={
            travelSaleVariant === TRAVEL_SALE_ACTIVE
              ? textConstants.TRAVEL_SALES_EVENT_ACTIVE_CTA
              : textConstants.TRAVEL_SALES_EVENT_LEADUP_CTA
          }
        />
      );
    }

    return undefined;
  }, [travelSaleVariant, matchesMobile, history]);

  const { isTravelApp } = useIsTravelApp();

  useEffect(() => {
    if (params.section === "credits" || (!params.section && !isTravelApp)) {
      setMobileSectionToDisplay("credits");
    } else if (params.section === "offers") {
      setMobileSectionToDisplay("offers");
    } else if (params.section === "faq") {
      setMobileSectionToDisplay("credits");
    }
  }, [params.section]);

  return (
    <Box
      className={clsx("travel-wallet-landing-root", {
        mobile: matchesMobile,
        xmod: isXmodRedesign,
      })}
    >
      {areOfferDetailsLoading && (
        <LoadingPopup
          open={areOfferDetailsLoading}
          indicator={B2BSpinner}
          message={textConstants.LOADING_WALLET}
          classes={[
            "offers-loading-modal",
            ...(matchesMobile ? ["mobile"] : []),
          ]}
        />
      )}
      {!isTravelApp && (
        <SecondaryHeader
          title={textConstants.REDESIGN_HEADER_TITLE_WALLET}
          subtitle={
            matchesMobile ? undefined : textConstants.HEADER_SUBTITLE_WALLET
          }
          breadCrumbLinks={headerBreadCrumbLinks}
          className="travel-wallet"
          rightContent={rightContent}
        />
      )}
      {!isTravelApp &&
        (matchesMobile ? (
          <PageTabNavigations
            navItems={[
              ...[
                {
                  label: `Travel credits`,
                  onClick: () => setMobileSectionToDisplay("credits"),
                },
              ],
              ...[
                {
                  label: `Travel offers${
                    offers && offers.length > 0 ? ` (${offers.length})` : ""
                  }`,
                  onClick: () => setMobileSectionToDisplay("offers"),
                },
              ],
            ]}
            className="mobile"
            selectedTab={selectedTab}
          />
        ) : (
          <PageTabNavigations
            navItems={[
              ...[
                {
                  label: `My travel credits${
                    credit
                      ? ` (${CurrencyFormatters.get(
                          credit.amount.currency,
                        ).format(credit.amount.amount * -1)})`
                      : ""
                  }`,
                  iconName: "piggy-bank-icon",
                  onClick: () => scrollToSection("credits"),
                },
              ],
              ...[
                {
                  label: `My travel offers${
                    offers && offers.length > 0 ? ` (${offers.length})` : ""
                  }`,
                  iconName: "offer-tag",
                  onClick: () => scrollToSection("offers"),
                },
                {
                  label: "Frequently asked questions",
                  iconName: "question-circle",
                  onClick: () => scrollToSection("faq"),
                },
              ],
            ]}
            selectedTab={selectedTab}
            ariaLabel="travel offers tabs"
          />
        ))}
      {matchesMobile && travelSaleVariant !== CONTROL && (
        <TravelSalesEventBanner
          variant={
            travelSaleVariant === TRAVEL_SALE_ACTIVE ? "default" : "with-timer"
          }
          onClick={() =>
            history.push(
              `${PATH_TRAVEL_SALE}?entryType=my_travel_credits_offers`,
            )
          }
          subtitle={
            travelSaleVariant === TRAVEL_SALE_ACTIVE
              ? textConstants.TRAVEL_SALES_EVENT_ACTIVE_SUBTITLE
              : textConstants.TRAVEL_SALES_EVENT_LEADUP_SUBTITLE
          }
          buttonText={
            travelSaleVariant === TRAVEL_SALE_ACTIVE
              ? textConstants.TRAVEL_SALES_EVENT_ACTIVE_CTA
              : textConstants.TRAVEL_SALES_EVENT_LEADUP_CTA
          }
          isMobile
        />
      )}

      {!areOfferDetailsLoading && (
        <>
          {(matchesMobile ? mobileSectionToDisplay === "credits" : true) && (
            <div id="credits" ref={creditsRef}>
              {isCorpTenant(getConfigTenant()) && isSpendAndGetOfferEnabled && (
                <div className={"spend-and-get-offer-wrapper"}>
                  <SpendAndGetOfferWalletSection
                    className={"spend-and-get-offer-section"}
                    onBookCtaClick={() => {
                      history.push(BASE_PATH_HOME);
                    }}
                  />
                </div>
              )}
              <Credits
                isTravelCreditHistoryExperiment={
                  isTravelCreditHistoryExperiment
                }
                isCreditAndOfferStackingExperimentV1={
                  isCreditAndOfferStackingExperimentV1
                }
                isAnnualTravelCreditsExperiment={
                  isAnnualTravelCreditsExperiment
                }
                isSpendAndGetOfferEnabled={isSpendAndGetOfferEnabled}
              />
            </div>
          )}
          {(matchesMobile ? mobileSectionToDisplay === "offers" : true) &&
            (travelSaleVariant !== TRAVEL_SALE_ACTIVE || !matchesMobile) && (
              <div id="offers" ref={offersRef}>
                <OffersList />
              </div>
            )}
          <div id="faq" ref={faqRef}>
            <FAQ />
          </div>
          {!isTravelApp && (
            <div
              id="support"
              className={clsx({ mobile: matchesMobile, xmod: isXmodRedesign })}
            >
              <Support />
            </div>
          )}
        </>
      )}
    </Box>
  );
};
